import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import classnames from 'classnames';
import moment from 'moment';
import { keys, isEmpty, isNil, contains } from 'ramda';
import { get } from 'object-path';

import { Circle, Check } from 'react-feather';

import Attachment from '../Attachment/AttachmentContainer';
import Avatar from '../Avatar';
import MessageContent from '../MessageContent/MessageContentContainer';

import { MESSAGE, ATTACHMENT } from '../../constants/AppConstants';
import * as ChatRoles from '../../constants/Roles';

class Message extends Component {
  handleRetryMessageFail = () => this.props.retryMessageFail(this.props.message);
  handleDeleteMessageFail = () => this.props.deleteMessageFail(this.props.message);

  getSelectedMessageClass = () => {
    const { selectedMessages, message } = this.props;
    return !isEmpty(selectedMessages) && selectedMessages[MESSAGE] && contains(message.id, selectedMessages[MESSAGE]);
  };

  isOwnMessage = (sender, identity) => get(sender, 'id') === get(identity, 'id');

  showSelectMessage = () => this.props.isSelectingMessages && !isEmpty(this.props.message.content) && !this.props.isFailed;

  render() {
    const { message, sender, attachments, selectedMessages, isSelectingMessages, selectMessage, identity } = this.props;
    const { id, chatId, content, timeSent, shouldShowTimeSent, isChained, isFailed, isFirstInChainedMessage } = message;
    const attachmentsIds = keys(attachments);

    return (
      <div
        id={`message-${id}`}
        className={classnames({
          'message': true,
          'own-message': this.isOwnMessage(sender, identity),
          'chained-message': isChained,
          'is-first-in-chained-message': isFirstInChainedMessage,
          'failed': isFailed,
          'selection-view': isSelectingMessages,
        })}
        data-qa={'message'}
      >
        {(isChained || content) &&
          <div className={classnames({
            'message-chunk': true,
            'selected-message': this.getSelectedMessageClass(),
            'empty-meta': isChained && !content,
          })}>
            <div className="message-gutter">
              {this.showSelectMessage() &&
                <SelectMessage
                  chatId={chatId}
                  messageId={id}
                  selectableType={MESSAGE}
                  selectMessage={selectMessage}
                  selectedMessages={selectedMessages}
                />
              }
              {!isChained &&
                <span className={classnames({
                  'avatar': true,
                  'in-selection-mode': isSelectingMessages && isEmpty(content) && !isFailed,
                })}>
                  <Avatar
                    firstName={get(sender, 'firstName')}
                    lastName={get(sender, 'lastName')}
                    type={this.isOwnMessage(sender, identity) ? ChatRoles.SENDER : ChatRoles.RECEIVER}
                  />
                </span>
              }
            </div>
            <div className="message-container">
              {!isChained && <strong>{`${get(sender, 'firstName')} ${get(sender, 'lastName')}`}</strong>}
              {(!isChained || (content && shouldShowTimeSent)) &&
                <span className="message-timestamp" data-qa={'messageDate'}>
                  {moment(timeSent).format('h:mm a')}
                </span>
              }
              {content && <MessageContent content={content}/>}
            </div>
          </div>
        }
        {attachmentsIds.map(attachmentId =>
          <div
            className={classnames({
              'message-chunk': true,
              'with-attachment': true,
              'selected-message': !isEmpty(selectedMessages) && selectedMessages[ATTACHMENT] && contains(attachmentId, selectedMessages[ATTACHMENT]),
            })}
            key={attachmentId}
          >
            <div className="message-gutter">
              {isSelectingMessages &&
                <SelectMessage
                  chatId={chatId}
                  messageId={attachmentId}
                  selectableType={ATTACHMENT}
                  selectMessage={selectMessage}
                  selectedMessages={selectedMessages}
                />
              }
              {!isChained && !content &&
                <span className="avatar">
                  <Avatar
                    firstName={get(sender, 'firstName')}
                    lastName={get(sender, 'lastName')}
                    type={this.isOwnMessage(sender, identity) ? ChatRoles.SENDER : ChatRoles.RECEIVER}
                  />
                </span>
              }
            </div>
            <div className="message-container">
              {!isChained && !content && <strong>{`${get(sender, 'firstName')} ${get(sender, 'lastName')}`}</strong>}
              {(!isChained || shouldShowTimeSent) && !content &&
                <span className="message-timestamp" data-qa={'messageDate'}>
                  {moment(timeSent).format('h:mm a')}
                </span>
              }
              <Attachment attachment={attachments[attachmentId]}/>
            </div>
          </div>
        )}

        {isFailed &&
          <div className="failed-message-options message-chunk">
            <div className="message-gutter"></div>
            <div className="message-container">
              <span className="failed-message-title">Send Failed</span>
              <span>
                <span className="failed-message-option" onClick={this.handleRetryMessageFail}>Retry</span>
                <span className="failed-message-option" onClick={this.handleDeleteMessageFail}>Delete</span>
              </span>
            </div>
          </div>
        }
      </div>
    );
  }
}

class SelectMessage extends Component {
  handleSelectMessage = () => {
    const { chatId, messageId, selectableType, selectMessage } = this.props;
    selectMessage(chatId, messageId, selectableType);
  };

  showSelectMessages = () => {
    const { messageId, selectableType, selectedMessages } = this.props;
    return !isEmpty(selectedMessages) && !isNil(selectedMessages[selectableType]) && contains(messageId, selectedMessages[selectableType]);
  };

  render() {
    return (
      <button
        className="select-message"
        onClick={this.handleSelectMessage}
        data-qa="selectMessage"
      >
        {this.showSelectMessages()
          ? (
            <span className="selected-message">
              <span className="desktop-icon"><Check size={18} /></span>
              <span className="mobile-icon"><Check size={30} /></span>
            </span>
          ) : (
            <Fragment>
              <span className="desktop-icon"><Circle size={24} /></span>
              <span className="mobile-icon"><Circle size={36} /></span>
            </Fragment>
          )
        }
      </button>
    );
  }
}

Message.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    content: PropTypes.string,
    timeSent: PropTypes.oneOfType([PropTypes.objectOf(Date()), PropTypes.string]).isRequired,
    shouldShowTimeSent: PropTypes.bool,
  }),
  sender: PropTypes.object,
  identity: PropTypes.object,
  attachments: PropTypes.object,
  selectedMessages: PropTypes.object,
  selectMessage: PropTypes.func,
  retryMessageFail: PropTypes.func.isRequired,
  deleteMessageFail: PropTypes.func.isRequired,
};

export default Message;
